import { Fragment, useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { HttpResponse } from 'shell/models/http-response';
import { ResponseAnprData, ResponseAnprSaveData } from 'shell/models/person';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { __PERSON_API_URL__, __PERSON_NEW_API_URL__ } from 'shell/constants/environment';
import { EditCohabitationCrumbs } from 'cohabitation/constants/breadcrumb';
import { transformerCohabitation } from 'cohabitation/utils/transformer-cohabitation';
import { CohabitationDetailForm } from 'cohabitation/view-models/detail-cohabitation';
import { Box, Grid } from '@mui/material';
import { PmiButton } from 'shell/components/shared/button';
import CohabitationEditStep from 'cohabitation/components/edit/child';
import CohabitationEditPageButtons from 'cohabitation/components/edit/buttons';
import { Form, Formik } from 'formik';
import { cohabitationFormSchema } from 'cohabitation/constants/validation-schema-detail';
import { transformResponseSaveDataAnpr } from 'shell/utils/save-data-anpr-response-transformer';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { SaveDataAnpr } from 'shell/view-models/save-data-anpr';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { ItemVM } from 'shell/view-models/item';
import { transformerRequestEditCohabitation } from 'cohabitation/utils/transform-request-edit-cohabitation';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';

const CohabitationEditPage = (): React.ReactElement => {
  const [queryParams] = useSearchParams();
  // query params
  const nationalSearch = JSON.parse(queryParams.get('ricercaNazionale') || 'false');
  const anprId = queryParams.get('anprId') || '';

  const [anpr] = useHttpFetch<HttpResponse<ResponseAnprData>, CohabitationDetailForm>(
    `${__PERSON_NEW_API_URL__}/persona`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {
          criteriRicerca: {
            identificativoANPR: anprId,
            tipoScheda: '2',
          },
          datiRichiesta: {
            ricercaNazionale: nationalSearch ? '1' : '0',
          },
        },
      },
      transformer: transformerCohabitation,
    }
  );

  const [stepState, setStepState] = useState<number>(0);

  const handleStep = useCallback(
    (step) => {
      setStepState(step);
    },
    [setStepState]
  );

  const [responseData, subscribe] = useHttpFetch<HttpResponse<ResponseAnprSaveData>, SaveDataAnpr>(
    `${__PERSON_API_URL__}/famiglia-convivenza/mutazione`,
    {
      options: {
        method: 'POST',
      },
      transformer: transformResponseSaveDataAnpr,
    }
  );

  const castedSavedData = responseData.data as SaveDataAnpr;

  // visibility
  const [visibility, setVisibility] = useState<{ error?: boolean; warning?: boolean }>({
    error: undefined,
    warning: undefined,
  });

  // on change visibility
  const onVisibility = useCallback(
    (open, type) => {
      const visibilityMessage = type === 'error' && !open ? false : open;

      setVisibility((oldState) => ({ ...oldState, [type]: visibilityMessage }));
    },
    [setVisibility]
  );

  const navigate = useNavigate();

  const handleSubmit = useCallback(
    (values: CohabitationDetailForm) => {
      const data = transformerRequestEditCohabitation(values);
      (
        subscribe({
          data,
        }) as ResponsePromise<HttpResponse<ResponseAnprSaveData>, SaveDataAnpr>
      ).then((response) => {
        const castedResponse = response as SaveDataAnpr;
        setVisibility({ error: undefined, warning: undefined });

        if (castedResponse?.errors?.length) {
          window.scrollTo(0, 0);
          return;
        }

        toast.success('Operazione eseguita con successo');
        navigate('/convivenza/ricerca');
      });
    },
    [navigate, subscribe, setVisibility]
  );

  //Errors
  const errorMessage = useMemo(() => {
    const errorToVisualize: { visibility?: boolean; title?: string; messages?: Array<ItemVM> } = {
      visibility: false,
      title: '',
    };

    if (castedSavedData?.errors?.length && visibility.error === undefined) {
      errorToVisualize.title =
        castedSavedData?.errors?.length > 1
          ? `Ci sono ${castedSavedData?.errors?.length} errori`
          : `C'è 1 errore`;
      errorToVisualize.messages = castedSavedData?.errors;
      errorToVisualize.visibility = true;
      return errorToVisualize;
    }

    errorToVisualize.visibility = visibility.error;

    return errorToVisualize;
  }, [visibility.error, castedSavedData]);

  const handleExit = useCallback(() => {
    navigate('/convivenza/ricerca');
  }, [navigate]);

  return (
    <Fragment>
      <Breadcrumbs items={EditCohabitationCrumbs} />
      {anpr.isLoading || anpr.pristine || anpr.hasErrors ? undefined : (
        <Formik
          initialValues={anpr.data as CohabitationDetailForm}
          onSubmit={handleSubmit}
          validationSchema={cohabitationFormSchema}
          validateOnMount
        >
          <Form>
            <Grid container spacing={2}>
              <Grid item xs={12} md={2}>
                <CohabitationEditPageButtons stepState={stepState} handleStep={handleStep} />
                <div className="pmi-pt-4">
                  <div className="pmi-pb-2">
                    <PmiButton
                      color="cdmBrandThird"
                      variant="outlined"
                      fullWidth
                      type="submit"
                      id="confirm"
                    >
                      Conferma
                    </PmiButton>
                  </div>
                  <div className="pmi-pb-2">
                    <PmiButton
                      color="cdmBrand"
                      variant="contained"
                      fullWidth
                      onClick={handleExit}
                      id="deny-operation"
                    >
                      <Box
                        sx={{
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Annulla Operazione
                      </Box>
                    </PmiButton>
                  </div>
                </div>
              </Grid>
              <Grid item xs={12} md={10}>
                <Grid container spacing={2} item xs={12}>
                  <Grid item xs={12}>
                    <SearchMessages
                      type={MessageType.Error}
                      title={errorMessage.title || ''}
                      messages={errorMessage.messages || []}
                      visibility={errorMessage.visibility || false}
                      onChangeVisibility={onVisibility}
                    />
                    <SearchMessages
                      type={MessageType.Warning}
                      title={`Ci sono ${castedSavedData?.warnings?.length || ''} warning`}
                      messages={castedSavedData?.warnings || []}
                      visibility={
                        visibility.warning !== undefined
                          ? visibility.warning
                          : (castedSavedData?.warnings || [])?.length > 0
                      }
                      onChangeVisibility={onVisibility}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <CohabitationEditStep stepState={stepState} />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Form>
        </Formik>
      )}
    </Fragment>
  );
};

CohabitationEditPage.displayName = 'Cohabitation Edit page component';

export default CohabitationEditPage;
